/**
 * The API, scoped to the deck this page is showing.
 *
 * Every call carries the slug, because one hostname now serves several
 * presentations and a call with no deck in it would be ambiguous. The slug is
 * read from the URL once, at load.
 */
import { DEFAULT_DECK_ALIAS } from './slug.ts';

export type Stage = 'password' | 'email' | 'code' | 'ready';

/** How this deck lets people in — mirrors the server's AccessPolicy. */
export interface AccessPolicy {
  passwordRequired: boolean;
  collectEmail: boolean;
  requireEmailVerification: boolean;
}

export interface SessionState {
  stage: Stage;
  email?: string;
  title?: string;
  allowDownload?: boolean;
  /** Public reCAPTCHA v3 key; empty when the captcha is switched off. */
  recaptchaSiteKey?: string;
  /** Absent when the deck could not be resolved at all. */
  access?: AccessPolicy;
  /** True when this route names no deck we serve. */
  unknownDeck?: boolean;
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly data: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(body),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    throw new ApiError(String(data.error ?? `http_${res.status}`), res.status, data);
  }
  return data as T;
}

/**
 * Which deck this page is for.
 *
 * The first path segment, or the alias meaning "whatever is at /". Read once —
 * a deck viewer never navigates between decks, and re-reading per call would
 * make a stray history change silently retarget the API.
 */
export const deckSlug: string = (() => {
  const first = window.location.pathname.split('/').filter(Boolean)[0] ?? '';
  return first || DEFAULT_DECK_ALIAS;
})();

const base = `/api/${encodeURIComponent(deckSlug)}`;

export const api = {
  async session(): Promise<SessionState> {
    const res = await fetch(`${base}/session`, { credentials: 'same-origin' });
    // A 404 means this route names no deck. That is a real state the UI has to
    // render — the request-access page — not an error to swallow.
    if (res.status === 404) return { stage: 'password', unknownDeck: true };
    if (!res.ok) return { stage: 'password' };
    return (await res.json()) as SessionState;
  },

  gate(password: string, captchaToken: string) {
    return post<{ ok: true; stage: Stage }>(`${base}/gate`, { password, captchaToken });
  },

  requestCode(email: string) {
    return post<{ ok: true; ttlMinutes: number; stage: Stage }>(`${base}/auth/request-code`, { email });
  },

  verify(email: string, code: string) {
    return post<{ ok: true; email: string }>(`${base}/auth/verify`, { email, code });
  },

  logout() {
    return post<{ ok: true }>(`${base}/auth/logout`, {});
  },

  /**
   * The manifest for this deck.
   *
   * `version` previews a specific build and is honoured only for a signed-in
   * admin; the server serves the published one to everyone else.
   */
  async deck(version?: string): Promise<Response> {
    const url = version ? `${base}/deck?v=${encodeURIComponent(version)}` : `${base}/deck`;
    return fetch(url, { credentials: 'same-origin' });
  },

  /** The public site key, available even on a route that names no deck. */
  async siteConfig(): Promise<{ recaptchaSiteKey?: string }> {
    try {
      const res = await fetch('/api/site', { credentials: 'same-origin' });
      return res.ok ? ((await res.json()) as { recaptchaSiteKey?: string }) : {};
    } catch {
      return {};
    }
  },

  /** Ask to be let in — from an unknown route, or from a gate without the password. */
  requestAccess(email: string, message: string, captchaToken: string) {
    return post<{ ok: true }>('/api/access-request', { slug: deckSlug, email, message, captchaToken });
  },

  /**
   * Engagement beacons. Fire-and-forget: a failed report must never interrupt
   * someone reading the deck.
   */
  events(payload: unknown): void {
    const body = JSON.stringify(payload);
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${base}/events`, new Blob([body], { type: 'application/json' }));
        return;
      }
    } catch {
      /* fall through to fetch */
    }
    void fetch(`${base}/events`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body,
      keepalive: true,
    }).catch(() => undefined);
  },
};

/**
 * Human-readable text for the error codes the API returns. Accepts either an
 * ApiError or a bare `{ code }`, so client-side failures (a blocked captcha
 * script, say) can reuse the same copy.
 */
export function messageFor(err: unknown): string {
  const code =
    err instanceof ApiError
      ? err.code
      : typeof (err as { code?: unknown })?.code === 'string'
        ? String((err as { code: string }).code)
        : 'unknown';
  switch (code) {
    case 'incorrect_password':
      return 'That password is not right. Please check and try again.';
    case 'password_required':
      return 'Please enter the password.';
    case 'invalid_email':
      return 'That does not look like a valid email address.';
    case 'incorrect_code': {
      const left = err instanceof ApiError ? (err.data.attemptsLeft as number | undefined) : undefined;
      return left && left > 0
        ? `That code is not right — ${left} ${left === 1 ? 'attempt' : 'attempts'} left.`
        : 'That code is not right.';
    }
    case 'code_expired':
      return 'That code has expired. Request a new one.';
    case 'too_many_attempts':
      return 'Too many incorrect attempts. Request a new code.';
    case 'send_failed':
      return 'We could not send the email just now. Please try again in a moment.';
    case 'gate_required':
      return 'Your session expired. Please enter the password again.';
    case 'captcha_failed':
      return 'The security check did not pass. Please try again, or use a different browser.';
    case 'recaptcha_unavailable':
      return 'The security check could not load. It may be blocked by an ad blocker, an extension, or a corporate network — try disabling those, or use another browser.';
    case 'email_not_collected':
      return 'This presentation does not ask for an email address.';
    case 'verification_not_required':
      return 'This presentation does not use emailed codes.';
    case 'no_such_deck':
      return 'That link does not match a presentation we have.';
    case 'revoked':
      return 'Access to this presentation has been withdrawn.';
    default:
      if (err instanceof ApiError && err.status === 429) {
        return 'Too many attempts. Please wait a few minutes and try again.';
      }
      return 'Something went wrong. Please try again.';
  }
}
